import { ElementRef, Injectable, TemplateRef, ViewContainerRef } from '@angular/core';
import { Overlay, OverlayContainer, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ESCAPE } from '@angular/cdk/keycodes';
import { Subscription } from 'rxjs';

/**
 * The toolbar's single drawer.
 *
 * Every control that needs more room than a button — vessel-type chips, track
 * spans, playback speeds — renders its content through this one service instead
 * of hand-rolling its own popover. That buys three things the old per-control
 * `open` flags could not: exactly ONE panel open at a time, dismissal on
 * outside-click/Escape, and a panel that flips or shifts to stay on screen when
 * its trigger sits near an edge.
 *
 * Content is a `TemplateRef`, not a component type, so a control's panel keeps
 * that control's own `ctrl`/`state$`/methods with zero input plumbing — adding a
 * new panel is markup only.
 */
@Injectable({ providedIn: 'root' })
export class ToolbarDrawerService {
  /**
   * The on-map toolbars sit at z-index 1200 while CDK's overlay container
   * defaults to 1000, so a drawer would render behind the bar that opened it.
   * The container is shared by every dialog and menu in the app, so instead of
   * raising it globally we tag it only while a drawer is actually open.
   */
  private static readonly CONTAINER_CLASS = 'tb-drawer-host';

  private _overlayRef: OverlayRef | null = null;
  private _active: TemplateRef<any> | null = null;
  private _subs = new Subscription();

  /**
   * Where the shared overlay container normally lives, remembered while a drawer
   * has borrowed it into a fullscreen element (see `_mountInFullscreen`).
   */
  private _containerHome: { parent: Node; next: Node | null } | null = null;

  constructor(private overlay: Overlay, private overlayContainer: OverlayContainer) {}

  /** The template currently on screen (null when the drawer is closed). */
  get active(): TemplateRef<any> | null {
    return this._active;
  }

  /** True when `tpl` is the open panel — drives the trigger's active styling. */
  isOpen(tpl: TemplateRef<any>): boolean {
    return this._active === tpl;
  }

  /** Open `tpl` anchored to `origin`, or close it if it is already showing. */
  toggle(
    tpl: TemplateRef<any>,
    vcr: ViewContainerRef,
    origin: ElementRef | HTMLElement,
  ): void {
    if (this.isOpen(tpl)) {
      this.close();
      return;
    }
    this.open(tpl, vcr, origin);
  }

  open(
    tpl: TemplateRef<any>,
    vcr: ViewContainerRef,
    origin: ElementRef | HTMLElement,
  ): void {
    // Opening one drawer always closes another — that is the whole point.
    this.close();

    // Before the overlay is created, so the pane is built in its final home and
    // the open animation does not run in a place the browser is not painting.
    this._mountInFullscreen();

    const originEl = origin instanceof ElementRef ? origin : new ElementRef(origin);
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(originEl)
      .withPush(true)
      .withFlexibleDimensions(false)
      // Keep a margin from the window edges so a tall panel is never clipped —
      // the drawer caps its own height and scrolls its body past that point.
      .withViewportMargin(12)
      .withPositions([
        // The toolbar sits at the bottom of the map, so the drawer rises out of
        // it by default and only drops below when there is no room above.
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -10 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -10 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -10 },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 10 },
      ]);

    this._overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      // No backdrop: a backdrop would swallow clicks meant for the map and grey
      // out the very vessels the panel is about.
      hasBackdrop: false,
      panelClass: 'tb-drawer-overlay',
    });

    this._overlayRef.attach(new TemplatePortal(tpl, vcr));
    this._active = tpl;
    this.overlayContainer
      .getContainerElement()
      .classList.add(ToolbarDrawerService.CONTAINER_CLASS);

    this._subs = new Subscription();
    this._subs.add(
      this._overlayRef.outsidePointerEvents().subscribe((event) => {
        // Ignore clicks on the trigger itself, or it would close here and
        // immediately reopen from the trigger's own click handler.
        const target = event.target as Node;
        if (originEl.nativeElement?.contains?.(target)) return;
        this.close();
      }),
    );
    this._subs.add(
      this._overlayRef.keydownEvents().subscribe((event) => {
        if (event.keyCode === ESCAPE) this.close();
      }),
    );

    // Maximizing or restoring the map moves the toolbar this drawer is anchored
    // to and changes which tree is being painted, so the panel is closed rather
    // than left pointing at a bar that is no longer where it was.
    const onFullscreenChange = () => this.close();
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    this._subs.add(() => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    });
  }

  close(): void {
    this._subs.unsubscribe();
    this._subs = new Subscription();
    this._overlayRef?.dispose();
    this._overlayRef = null;
    this._active = null;
    // Hand the shared container back to its default stacking order — and to its
    // default place in the document, if this drawer moved it.
    this.overlayContainer
      .getContainerElement()
      .classList.remove(ToolbarDrawerService.CONTAINER_CLASS);
    this._restoreContainer();
  }

  /** Close only if `tpl` is the open panel (used when a control is destroyed). */
  closeIfOpen(tpl: TemplateRef<any> | null | undefined): void {
    if (tpl && this.isOpen(tpl)) this.close();
  }

  // ── Fullscreen (the maximized map) ──────────────────────────────────────────

  /**
   * Lend the shared overlay container to the maximized map.
   *
   * "Maximize" is native fullscreen on the map element, and a fullscreen element
   * is the ONLY subtree the browser paints — everything else in the document is
   * skipped, top layer or not. CDK's overlay container is a child of <body>, a
   * SIBLING of the map, so a drawer opened while maximized was mounted, sized
   * and positioned correctly into a part of the page that is simply not on
   * screen. The toolbars survive because they live inside the map element; the
   * drawer, alone among the on-map chrome, does not.
   *
   * Moving the container inside the fullscreen element puts it back in the
   * painted tree. It is the container every dialog, menu and select in the app
   * shares, so the move is undone in `close()` — the same borrow-and-return the
   * z-index class already does. Stacking still works out: inside the map the
   * container is a sibling of the toolbars (1200) and keeps its 1300.
   */
  private _mountInFullscreen(): void {
    const fs = this._fullscreenElement();
    if (!fs) return;
    const container = this.overlayContainer.getContainerElement();
    if (fs.contains(container)) return;
    this._containerHome = { parent: container.parentNode!, next: container.nextSibling };
    fs.appendChild(container);
  }

  /** Put the container back where it was found; a no-op if it never moved. */
  private _restoreContainer(): void {
    const home = this._containerHome;
    this._containerHome = null;
    if (!home) return;
    const container = this.overlayContainer.getContainerElement();
    // The remembered sibling can be gone by now (Angular churns the map's
    // siblings); appending to the same parent is the right fallback.
    if (home.next && home.next.parentNode === home.parent) {
      home.parent.insertBefore(container, home.next);
    } else {
      home.parent.appendChild(container);
    }
  }

  /** `webkitFullscreenElement` for the same browsers `toggleFullscreen` covers. */
  private _fullscreenElement(): HTMLElement | null {
    const doc = document as any;
    return (doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null) as HTMLElement | null;
  }
}
